// @ts-nocheck
import { memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Field } from 'react-final-form';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef, getTargetEntityFounderSelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { PetitionOrganization } from 'KnmErknmPackage/ErkmAgreementsModule/components/AgreementsPetitionOrganization/PetitionOrganization';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { IErknmFormAgreementPetitionOrganization } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreementPetitionOrganization';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { agreementsTypeSelectOptions } from 'KnmPackage/MainModule/constants/agreementsTypeSelectOptions';
import { IGeneralResultDecision } from 'KnmErknmPackage/ErkmAgreementsModule/interfaces/IGeneralResultDecision';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import styles from './AgreementsPetitionOrganization.module.scss';
import { AgreementsPetitionAttachment } from './AgreementsPetitionAttachment';

interface IAgreementsPetitionOrganizationProps {
  id: string;
  name: string;
  watchOnly: boolean;
  agreement: IErknmFormAgreement;
  resultDecisions: IGeneralResultDecision[] | undefined;
}

interface IAgreementsPetitionOrganizationStoreProps {
  formRefKnm: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
}

const AgreementsPetitionOrganizationWithoutStore = memo(
  ({
    id,
    name,
    watchOnly,
    agreement,
    formRefKnm,
    decisionFormRef,
    resultDecisions,
    subjectTypeOptions,
  }: IAgreementsPetitionOrganizationProps & IAgreementsPetitionOrganizationStoreProps) => {
    const { isKnm, isPm, isDecision } = useContext(AgreementsContext);
    const fieldName = `${name}.petitionOrganization`;

    const formRef = isDecision ? decisionFormRef : formRefKnm;

    useEffect(() => {
      if (agreement.prescription) {
        formRef?.current.change(`${name}.petitionOrganizationType`, agreementsTypeSelectOptions[0]);
        onChangeType(agreementsTypeSelectOptions[0]);
      } else if (!agreement.prescription && agreement.petitionOrganizationType) {
        formRef?.current.change(`${name}.petitionOrganizationType`);
        formRef?.current.change(fieldName);
      }
    }, [agreement.prescription]);

    /**
     * Объект организации в выборанном предписании
     */
    const organizationInPrescription = useMemo(() => {
      if (resultDecisions) {
        const resultDecision = resultDecisions.find(decision => decision.guid === agreement.prescription?.value);

        return resultDecision?.organization;
      }
    }, [resultDecisions, agreement]);

    /**
     * Фукнция изменения типа ходатайства
     */
    const onChangeType = useCallback(
      (typeOption: ISelectOption<number>) => {
        const subjectTypeFieldName = `${fieldName}.subjectType` as keyof IKnmForm;
        const innFieldName = `${fieldName}.inn` as keyof IKnmForm;
        const ogrnFieldName = `${fieldName}.ogrn` as keyof IKnmForm;
        const organizationNameFieldName = `${fieldName}.organizationName` as keyof IKnmForm;
        const kppFieldName = `${fieldName}.kpp` as keyof IKnmForm;
        const rafpNzaFieldName = `${fieldName}.rafpNza` as keyof IKnmForm;
        const countryFieldName = `${fieldName}.country` as keyof IKnmForm;
        const locationAddressFieldName = `${fieldName}.locationAddress` as keyof IKnmForm;
        const factualAddressFieldName = `${fieldName}.factualAddress` as keyof IKnmForm;
        const workAddressFieldName = `${fieldName}.workAddress` as keyof IKnmForm;
        const snilsFieldName = `${fieldName}.snils` as keyof IKnmForm;
        const documentFieldName = `${fieldName}.document` as keyof IKnmForm;
        const isAutocompleteFieldName = `${fieldName}.isAutocomplete` as keyof IKnmForm;
        const smevRequestStatusFieldName = `${fieldName}.smevRequestStatus` as keyof IKnmForm;
        const checkSubjectResultFieldName = `${fieldName}.checkSubjectResult` as keyof IKnmForm;

        if (!typeOption) {
          formRef?.current.change(fieldName as keyof IKnmForm);
        }

        formRef?.current.change(smevRequestStatusFieldName);
        formRef?.current.change(checkSubjectResultFieldName);

        if (typeOption.value === 1 && organizationInPrescription) {
          formRef?.current.change(
            subjectTypeFieldName,
            // @ts-ignore
            organizationInPrescription.subjectType || organizationInPrescription.subjectTypePm,
          );
          formRef?.current.change(innFieldName, organizationInPrescription.inn);
          formRef?.current.change(ogrnFieldName, organizationInPrescription.ogrn);
          formRef?.current.change(
            organizationNameFieldName,
            organizationInPrescription.organizationName || organizationInPrescription.name,
          );
          formRef?.current.change(kppFieldName, organizationInPrescription.kpp);
          formRef?.current.change(rafpNzaFieldName, organizationInPrescription.rafpNza);
          formRef?.current.change(countryFieldName, organizationInPrescription.country);
          formRef?.current.change(locationAddressFieldName, organizationInPrescription.locationAddress);
          formRef?.current.change(factualAddressFieldName, organizationInPrescription.factualAddress);
          formRef?.current.change(workAddressFieldName, organizationInPrescription.workAddress);
          formRef?.current.change(snilsFieldName, organizationInPrescription.snils);
          formRef?.current.change(documentFieldName, organizationInPrescription.document);
          formRef?.current.change(isAutocompleteFieldName, organizationInPrescription.isAutocomplete);
        } else {
          formRef?.current.change(
            subjectTypeFieldName,
            subjectTypeOptions?.find(i => i.code === 'UL'),
          );
          formRef?.current.change(innFieldName);
          formRef?.current.change(ogrnFieldName);
          formRef?.current.change(organizationNameFieldName);
          formRef?.current.change(kppFieldName);
          formRef?.current.change(rafpNzaFieldName);
          formRef?.current.change(countryFieldName);
          formRef?.current.change(locationAddressFieldName);
          formRef?.current.change(factualAddressFieldName);
          formRef?.current.change(workAddressFieldName);
          formRef?.current.change(snilsFieldName);
          formRef?.current.change(documentFieldName);
          formRef?.current.change(isAutocompleteFieldName);
        }
      },
      [formRef, organizationInPrescription, subjectTypeOptions],
    );

    return (
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Ходатайство контролируемого лица',
          field: `${name}.petitionOrganizationType`,
          isKnm,
          isPm,
          isDecision,
        })}
        className={styles.Wrapper}
      >
        <FieldBlock
          label={'Лицо, с которым заключается соглашение'}
          color="black"
          id={`${id}.petitionOrganizationType`}
          className={styles.FieldBlock}
        >
          <SelectInputField
            name={`${name}.petitionOrganizationType`}
            placeholder={'Лицо'}
            size="medium"
            options={agreementsTypeSelectOptions}
            disabled={watchOnly || !agreement.prescription}
            onChange={onChangeType}
          />
        </FieldBlock>
        <div className={styles.PetitionOrganizationWrapper}>
          <Field key={fieldName} name={fieldName}>
            {({ input }) => {
              const organization = (input.value as IErknmFormAgreementPetitionOrganization) || undefined;

              if (!organization) return null;

              return (
                <PetitionOrganization
                  name={fieldName}
                  isWatchOnly={watchOnly}
                  isNewOrganizationTemplate={agreement.petitionOrganizationType?.value === 2}
                  organization={organization}
                  parentId={`${id}.petitionOrganization`}
                />
              );
            }}
          </Field>
        </div>
        {agreement.petitionOrganizationType && (
          <AgreementsPetitionAttachment
            watchOnly={watchOnly}
            name={`${name}.petitionAttachment`}
            id={`${id}.petitionAttachment`}
            attachment={agreement?.petitionAttachment}
            formRef={formRef}
          />
        )}
      </FieldBlock>
    );
  },
);

const mapStateToProps = (state: IRootState): IAgreementsPetitionOrganizationStoreProps => ({
  formRefKnm: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  subjectTypeOptions: getTargetEntityFounderSelectOptions(state),
});

export const AgreementsPetitionOrganization = connect(mapStateToProps)(AgreementsPetitionOrganizationWithoutStore);
